/* eslint-disable react-hooks/exhaustive-deps */
import { EVENT } from "@/constants/event";
import { SHORTCUT } from "@/constants/shortcut";
import useAppStore from "@/context/store";
import { getAssociate, getGrocery, getSearchPlaceholder } from "@/services";
import { setSearch } from "@/tracker/shared/global";
import isEditingElement from "@/utils/isEditingElement";
import { isBackspace, isDown, isUp } from "@/utils/keyBoard";
import useRequest from "@/utils/useReq";
import { useEventListener, useKeyPress, useMemoizedFn } from "ahooks";

import { useSearchLimit } from "@/components/LoginModal/useLoginLimit";
import { TrackerDiv } from "@/components/Tracker";
import UploadDragger from "@/components/UploadDragger";
import { UPLOAD_STATUS } from "@/constants/uploadStatus";
import { removeEmoji } from "@/utils/str";
import { Dropdown } from "antd";
import { useRouter } from "next/router";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useOuterClick from "../../../utils/useOuterClick";
import HBSearch from "../../HBInput/search";
import SearchAssociate from "./Associate";
import styles from "./index.module.less";
import SearchSuggest from "./Suggest";
import useSearchHistory from "./useSearchHistory";
import useSearchScope from "./useSearchScope";

const SEARCH_SIMILAR_IMG_ACCEPT_LIST = ["jpg", "jpeg", "png", "gif", "webp"];
const SEARCH_SIMILAR_IMG_ACCEPT_MIME_TYPE_LIST = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];

/**
 * 顶部导航栏的搜索输入框，包含搜索下拉内容展示、搜索范围切换、搜索联想词
 * @returns
 */
export default function Search({
  className,
  style,
  hideSuggest = false,
  hideQuickSearch = false,
  initValue,
  bordered,
  isNewLayout = false,
  ...props
}) {
  const { user, config } = useAppStore(({ user, config }) => ({
    user,
    config,
  }));
  const { searchHis, addSearchHistory, clearSearchHistory } =
    useSearchHistory();
  const [isSearchImg, setIsSearchImg] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(UPLOAD_STATUS.INIT);

  const [searchValue, setSearchValue] = useState(initValue || "");

  /** 当前选中搜索联想词 ，-1 表示未选中任何词，选中联想词会将联想词替换输入框的内容*/
  const [selected, setAssociateSelected] = useState(-1);

  const {
    currentScopeIdRef,
    currentSearchScope,
    changeScope,
    getSearchParam,
    QuickSearch,
    pageScope,
    searchTypeName,
  } = useSearchScope();

  const router = useRouter();
  const searchRef = useRef(null);

  //#region 搜索下拉相关逻辑
  // 搜索下拉的显示隐藏
  const [ref, visible, setDropdownVisible] = useOuterClick(false);
  // 滚动隐藏搜索下拉

  const lastScrollY = useRef(0);
  const scrollCb = useCallback(() => {
    if (Math.abs(window.scrollY - lastScrollY.current) > 80) {
      setDropdownVisible(false);
    }
  }, []);

  const isHome = router.pathname === "/";
  // 搜索下拉显示时，监听滚动事件。滚动超过80px时，隐藏搜索下拉
  useEffect(() => {
    if (!isHome) {
      if (visible) {
        lastScrollY.current = window.scrollY;
        window.addEventListener("scroll", scrollCb);
        return () => {
          window.removeEventListener("scroll", scrollCb);
        };
      } else {
        window.removeEventListener("scroll", scrollCb);
      }
    }
  }, [visible, isHome]);
  // 下拉最大显示高度， 当屏幕高度不够时，下拉高度最高为屏幕高度
  const maxDropHeight = useMemo(() => {
    if (searchRef.current?.input) {
      const viewHeight = window.innerHeight;
      const top = searchRef.current.input?.getBoundingClientRect()?.bottom || 0;
      return viewHeight - top - 70;
    } else {
      return "auto";
    }
  }, [searchRef.current]);
  //#endregion

  // #region 搜索联想词
  const searchAssociate = useRequest(
    async (param: { q: string }) => {
      const isSearchPage = router.pathname === "/search";
      // 用户搜索页下不提供搜索联想词
      if ((isSearchPage && router.query?.urlname) || !param.q) return [];
      const data = await getAssociate(param);
      const list: {
        word: string;
        html: string;
        total?: number;
        type?: string;
      }[] = (data?.result || []).map((text) => {
        return {
          word: text,
          html: text
            .split(searchValue)
            .join(`<span class=${styles.match}>${searchValue}</span>`),
        };
      });

      // 推荐搜索素材
      if (data.material_count > 0) {
        return [
          {
            word: searchValue,
            html: `<span class=${styles.match}>${searchValue}</span>`,
            total: data.material_count,
            type: "material",
          },
          ...list,
        ];
      }

      return list;
    },
    {
      debounceWait: 300,
      manual: true,
    }
  );
  const associateData = searchAssociate.data || [];
  /** 上下按键选中搜索联想词，回车按键触发搜索 */
  const onKeyDown = useMemoizedFn((e) => {
    // 删除当前的搜索范围
    if (
      !searchValue ||
      (e.target.selectionStart === 0 && e.target.selectionEnd === 0)
    ) {
      if (isBackspace(e)) {
        // 删除的当前搜索范围
        setTimeout(() => {
          // 全选删除的时候，导致文案被重置
          changeScope(null);
        }, 0);
        return;
      }
    }

    // 上下按键选中搜索联想词
    if (!associateData.length) return;
    const max = (associateData?.length || 0) - 1;

    if (isUp(e)) {
      let next = selected - 1;

      if (next < 0) {
        next = max;
      }

      // 设置选中的联想词
      setAssociateSelected(next);
      // 替换输入框的内容
      setSearchValue(associateData[next].word);
      e.preventDefault();
    }

    if (isDown(e)) {
      let next = selected + 1;

      if (next > max) {
        next = 0;
      }

      setAssociateSelected(next);
      setSearchValue(associateData[next].word);
    }
  });
  // #endregion

  // 根据路由初始化搜索词，与搜索联想词
  useEffect(() => {
    const { q } = router.query;
    if (initValue && router.pathname.includes("topic-")) {
      setSearchValue(initValue);
    } else {
      setSearchValue(q);
    }
    if (q) {
      searchAssociate.run({ q: q as string });
    }
  }, [router.query, initValue]);

  const { onSearchLimitCheck } = useSearchLimit();

  /**
   * 统一搜索方法
   * @param value 搜索词
   * @param param1
   * @param param1.query 搜索参数
   * @param param1.type 搜索类型 埋点用 主要有：手动输入搜索词、联想搜索词、搜索历史词、热门搜索词等
   */
  const onSearch = useMemoizedFn(
    async (value: string, { query = {}, type } = {}) => {
      if (value.trim().length) {
        setDropdownVisible(false);

        let nextQuery = getSearchParam({ ...query, q: value });

        // 埋点数据写入
        try {
          if (type) {
            setSearch({
              search_word_type: type,
              search_type: "搜索栏-搜索",
            });
          } else if (typeof selected === "number" && selected >= 0) {
            const searchType = searchAssociate.data[selected].type;
            if (searchType) {
              nextQuery = {
                type: searchType,
                ...nextQuery,
              };
            }

            setSearch({
              search_word_type: `联想搜索词${searchType ? "-素材" : ""}`,
              search_type: "搜索栏-搜索",
            });
          } else {
            setSearch({
              search_word_type: "手动输入搜索词",
              search_type: "搜索栏-搜索",
            });
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error);
        }

        await onSearchLimitCheck();

        router.push({
          pathname: `/search`,
          query: nextQuery,
        });

        addSearchHistory(value);

        props.onSearch && props.onSearch(value);
        setAssociateSelected(-1);
      } else {
        setDropdownVisible(false);
        // 轻舟配置的搜索占位词才进行占位词搜索
        if (Array.isArray(placeholder)) {
          onSearch(
            removeEmoji(searchRef.current.getCurrentPlaceholder().value),
            {
              type: "搜索占位词",
            }
          );
        }
      }
    }
  );

  const onQuickSearchClick = useMemoizedFn((scope) => {
    changeScope(scope);

    setTimeout(() => {
      if (router.pathname === "/search") {
        onSearch(searchValue, { type: "切换搜索范围" });
      } else {
        searchRef.current?.input?.focus();
      }
    });
  });

  // 全局绑定搜索栏获取焦点事件
  useEffect(() => {
    const action = ({ detail = null }) => {
      searchRef.current?.input?.focus();
      changeScope(detail);
    };

    window.addEventListener(EVENT.SEARCH_BAR_FOCUS, action);

    return () => {
      window.removeEventListener(EVENT.SEARCH_BAR_FOCUS, action);
    };
  }, [router.query]);

  // 快捷切换搜索范围按钮
  const quickSearchBtns = useMemo(() => {
    if (hideQuickSearch || !QuickSearch) {
      return null;
    }

    return <QuickSearch changeScope={onQuickSearchClick} />;
  }, [QuickSearch, hideQuickSearch, onQuickSearchClick]);

  // 快捷键进入搜索
  useKeyPress(
    SHORTCUT.SEARCH,
    (evt) => {
      const editing = isEditingElement();
      if (editing) {
        return false;
      }

      evt.preventDefault();

      searchRef.current?.input?.focus();
    },
    {
      exactMatch: true,
    }
  );

  // 是否在用户自己的个人主页
  const isSelfUserPage = useMemo(() => {
    if (user) {
      return router.asPath.startsWith(`/user/${user?.urlname}`);
    }
  }, [router.asPath, user]);

  const [placeholder, setPlaceholder] = useState<string | string[]>("搜索");
  const [placeholderPrefix, setPlaceholderPrefix] = useState<string>("");

  // 缓存搜索占位词请求数据，避免切换页面时重复请求
  const cachePlaceholder = useRef<any>({});
  // 获取搜索占位词配置
  useEffect(() => {
    const pathname = router?.pathname;
    if (pathname) {
      // 在用户个人主页页面、画板详情页展示固定的搜索占位词
      if (
        pathname.includes("/user/[urlname]") &&
        !pathname.includes("/following")
      ) {
        setPlaceholder(`搜索${isSelfUserPage ? "我的" : "Ta的"}或全部`);
      } else if (pathname.includes("/boards/[boardId]")) {
        setPlaceholder("搜索该画板的采集或全部");
      } else if (
        // 首页、关注页、发现页、搜索页展示轻舟配置的搜索占位词
        ["", "searchMaterial"].includes(currentScopeIdRef.current) &&
        ["/", "/follow", "/discovery", "/search", "/materials"].includes(
          pathname
        )
      ) {
        // 剩下的页面根据轻舟配置的搜索占位词展示
        let position = "discovery_recommend";

        if (
          pathname.startsWith("/materials") ||
          router.query.type === "material"
        ) {
          position = "material";
        }

        // 其他搜索结果页不展示轻舟配置
        if (
          pathname === "/search" &&
          ![undefined, "pin", "material"].includes(router.query.type as string)
        ) {
          setPlaceholder("搜索");
          return;
        }

        Promise.all([
          // 轻舟配置的搜索占位词
          cachePlaceholder.current[position] ||
            getSearchPlaceholder({ position }),
          // 杂货铺配置搜索占位词前缀
          getGrocery<{
            placeholderPrefix: string;
          }>("zhrml1vh1b8"),
        ]).then(([qingzhou, conf]) => {
          if (qingzhou?.words && qingzhou?.words.length) {
            cachePlaceholder.current[position] = qingzhou;
            qingzhou?.words.sort((a, b) => a.priority - b.priority);
            setPlaceholder(qingzhou?.words.map((item) => item.recommend_word));
          }

          if (conf?.placeholderPrefix) {
            setPlaceholderPrefix(conf.placeholderPrefix);
          }
        });
      } else {
        setPlaceholder("搜索");
      }
    }
  }, [router.pathname, isSelfUserPage, pageScope.type]);

  const onUploadDone = useCallback((info) => {
    const file_id = info.id ? `&file_id=${info.id}` : "";
    router.push(
      `/similar?bucket=${info.bucket}&key=${info.key}${file_id}&width=${info.width}&height=${info.height}`
    );
    setDropdownVisible(false);
  }, []);

  const onSearchSimilarImg = useCallback(() => {
    setDropdownVisible(true);
    window._search_similar_img_visible = true;
    setIsSearchImg(true);
  }, [user]);

  useEventListener(EVENT.SEARCH_SIMILAR_IMG, onSearchSimilarImg);

  useEffect(() => {
    // 判断dropdown已经消失并且以图搜图不为上传状态
    let timer;
    if (!visible && uploadStatus !== UPLOAD_STATUS.UPLOADING) {
      // 300ms等动画结束后触发
      timer = setTimeout(() => {
        window._search_similar_img_visible = false;
        setIsSearchImg(false);
      }, 300);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [visible, uploadStatus]);

  const onUploadStatus = useCallback((status) => {
    setUploadStatus(status);
  }, []);

  return (
    <TrackerDiv
      ref={ref}
      className={[className, isNewLayout && styles.newLayout]
        .filter(Boolean)
        .join(" ")}
      data-search-word={searchValue}
      data-module-name="搜索栏"
    >
      <Dropdown
        placement="bottomCenter"
        overlay={
          isSearchImg ? (
            <UploadDragger
              onDone={onUploadDone}
              onStatus={onUploadStatus}
              acceptMimeTypes={SEARCH_SIMILAR_IMG_ACCEPT_MIME_TYPE_LIST}
              // 后端会将宽度大于480图片的尺寸压缩成480，后端那边要求宽高比不能超过1比5，所以现在高度2400
              maxHeight={2400}
              maxWidth={480}
              tip={`图片格式支持 ${SEARCH_SIMILAR_IMG_ACCEPT_LIST.join("、")}`}
            />
          ) : (
            <div
              data-gd-expose="dialog_expose"
              data-dialog-title="搜索下拉弹窗"
              data-module-name="搜索下拉弹窗"
              data-subject-name="弹窗"
              data-search-word={searchValue}
              data-repository-type={searchTypeName}
            >
              {searchValue ? (
                <SearchAssociate
                  data={associateData}
                  selected={selected}
                  onSelect={setAssociateSelected}
                  preAddOn={quickSearchBtns}
                  onSearch={onSearch}
                  wrapStyle={{
                    maxHeight: maxDropHeight,
                  }}
                />
              ) : (
                <SearchSuggest
                  searchHis={searchHis}
                  clearSearchHis={clearSearchHistory}
                  preAddOn={quickSearchBtns}
                  onSearch={onSearch}
                  setDropdownVisible={setDropdownVisible}
                  hideSuggest={hideSuggest}
                />
              )}
            </div>
          )
        }
        trigger={[]}
        visible={visible || uploadStatus === UPLOAD_STATUS.UPLOADING}
        getPopupContainer={() => {
          return ref.current;
        }}
        overlayClassName={styles["ant-dropdown"]}
      >
        <HBSearch
          {...props}
          ref={searchRef}
          id="hb_search_input"
          value={searchValue}
          placeholder={placeholder}
          placeholderPrefix={placeholderPrefix}
          showSearchSimilarBtn={config?.showSearchSimilar}
          preTags={[
            {
              ...currentSearchScope,
              clear: changeScope,
            },
          ]}
          bordered={bordered}
          onSearch={onSearch}
          onInput={(e: ChangeEvent<HTMLInputElement>) => {
            !visible && setDropdownVisible(true);
            const value = e.target.value;

            setSearchValue(value);
            // 重置联想词选中状态
            setAssociateSelected(-1);
            searchAssociate.run({ q: value });

            props.onInput && props.onInput(e);
          }}
          onFocus={(e) => {
            !visible && setDropdownVisible(true);
            setIsSearchImg(false);
            props.onClick && props.onClick(e);
          }}
          onClick={(e) => {
            !visible && setDropdownVisible(true);
            props.onClick && props.onClick(e);
          }}
          onClear={() => {
            changeScope(null);
            setSearchValue(null);
          }}
          onSearchSimilarImg={onSearchSimilarImg}
          onKeyDown={onKeyDown}
          style={style}
          isNewLayout={isNewLayout}
        />
      </Dropdown>
    </TrackerDiv>
  );
}
